import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { storeService, ProductSize, UniformSubcategory } from '../services/StoreService';
import { schoolMatches } from '../utils/schoolScope';
import './AdminStoreView.css';
import './StudentDetailPages.css';

function SuperAdminStoreView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('products');
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const prevOrdersRef = useRef([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const categories = ['Uniform', 'Books', 'Crafts', 'Accessories', 'Stationery', 'Other'];

  const getCategoryIcon = (category) => {
    const icons = { 'Uniform': '👕', 'Books': '📚', 'Crafts': '🎨', 'Accessories': '🎒', 'Stationery': '✏️', 'Other': '🛍️' };
    return icons[category] || '🛍️';
  };

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.warn('Could not play sound:', e);
    }
  }, []);

  const loadOrdersOnly = useCallback(async () => {
    try {
      const allOrders = await storeService.getAllOrders((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId));
      const prevCount = prevOrdersRef.current.length;
      setOrders(allOrders);
      const pendingCount = allOrders.filter(o => o.status === 'Pending').length;
      setNewOrdersCount(pendingCount);
      if (prevCount > 0 && allOrders.length > prevCount) {
        playNotificationSound();
      }
      prevOrdersRef.current = allOrders;
    } catch (e) {
      // silent
    }
  }, [adminData, playNotificationSound]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [allProducts, allOrders, storeAnalytics] = await Promise.all([
        storeService.getAllProducts((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId)),
        storeService.getAllOrders((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId)),
        storeService.getStoreAnalytics((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId)),
      ]);
      setProducts(allProducts);
      const prevCount = prevOrdersRef.current.length;
      setOrders(allOrders);
      const pendingCount = allOrders.filter(o => o.status === 'Pending').length;
      setNewOrdersCount(pendingCount);
      // Play sound if new orders arrived
      if (prevCount > 0 && allOrders.length > prevCount) {
        playNotificationSound();
      }
      prevOrdersRef.current = allOrders;
      setAnalytics(storeAnalytics);
    } catch (error) {
      console.error('Error loading store data:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData, playNotificationSound]);

  useEffect(() => {
    loadData();
    // Poll for new orders every 30 seconds
    const interval = setInterval(() => {
      loadOrdersOnly();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData, loadOrdersOnly]);

  const handleSave = async (product) => {
    await storeService.saveProduct({ ...product, schoolId: adminData?.schoolId || null });
    setShowAddEdit(false);
    setEditingProduct(null);
    loadData();
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Delete this product?')) {
      await storeService.deleteProduct(productId);
      loadData();
    }
  };

  const handleOrderStatus = async (orderId, status) => {
    await storeService.updateOrderStatus(orderId, status);
    setSelectedOrder(null);
    loadData();
  };

  const formatCurrency = (amount) => `EC$${(amount || 0).toFixed(2)}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return date.toLocaleDateString();
  };

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || '';

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading store...</p>
      </div>
    );
  }

  if (selectedOrder) {
    console.log('🔥 SuperAdminStore: Opened order detail', selectedOrder.id);
    return (
      <OrderDetailPage
        order={selectedOrder}
        schoolName={schoolNameFor(selectedOrder.schoolId) || selectedOrder.schoolId || 'N/A'}
        onAccept={() => handleOrderStatus(selectedOrder.id, 'Accepted')}
        onReject={() => handleOrderStatus(selectedOrder.id, 'Rejected')}
        onComplete={() => handleOrderStatus(selectedOrder.id, 'Completed')}
        onCancel={() => handleOrderStatus(selectedOrder.id, 'Cancelled')}
        onClose={() => setSelectedOrder(null)}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />
    );
  }

  return (
    <div className="admin-store-view">
      <div className="store-header">
        <h2>School Store</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <div className="store-tabs">
          <button className={view === 'products' ? 'active' : ''} onClick={() => setView('products')}>Products</button>
          <button className={view === 'orders' ? 'active' : ''} onClick={() => setView('orders')}>
            Orders
            {newOrdersCount > 0 && <span className="tab-badge">{newOrdersCount}</span>}
          </button>
          <button className={view === 'analytics' ? 'active' : ''} onClick={() => setView('analytics')}>Analytics</button>
        </div>
      </div>

      {view === 'products' && (
        <>
          {/* Category Filters */}
          <div className="admin-category-filters">
            <button className={!selectedCategory ? 'active' : ''} onClick={() => setSelectedCategory(null)}>All</button>
            {categories.map(cat => (
              <button key={cat} className={selectedCategory === cat ? 'active' : ''} onClick={() => setSelectedCategory(cat)}>
                {getCategoryIcon(cat)} {cat}
              </button>
            ))}
          </div>

          <div className="store-actions">
            <button className="add-product-btn" onClick={() => { setEditingProduct(null); setShowAddEdit(true); }}>
              + Add Product
            </button>
          </div>

          <div className="products-grid">
            {products.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🛍️</div>
                <h3>No Products</h3>
                <p>Add products to start selling</p>
              </div>
            ) : (
              products
                .filter(p => !selectedCategory || p.category === selectedCategory)
                .map(product => (
                <div key={product.id} className="store-product-card">
                  <div className="card-image-section">
                    {product.imageURL ? (
                      <img src={product.imageURL} alt={product.name} className="product-image" />
                    ) : (
                      <div className="product-icon-large">{getCategoryIcon(product.category)}</div>
                    )}
                  </div>
                  <div className="card-body">
                    <span className="card-badge">{product.category}</span>
                    <h4>{product.name}</h4>
                    {(schoolNameFor(product.schoolId) || product.schoolId) && (
                      <p className="product-meta" style={{ fontSize: '0.8rem', color: '#64748b' }}>🏫 {schoolNameFor(product.schoolId) || product.schoolId}</p>
                    )}
                    <div className="product-meta">
                      {product.subcategory && <span className="product-subcategory">{product.subcategory}</span>}
                      {product.size && <span className="product-size">{product.size}</span>}
                    </div>
                    <p className={`product-stock ${product.stockQuantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                      {product.stockQuantity > 0 ? `${product.stockQuantity} in stock` : 'Out of stock'}
                    </p>
                  </div>
                  <div className="card-footer">
                    <span className="product-price">{formatCurrency(product.price)}</span>
                    <div className="product-actions">
                      <button onClick={() => { setEditingProduct(product); setShowAddEdit(true); }}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDelete(product.id)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {view === 'orders' && (
        <div className="orders-list">
          {orders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No Orders</h3>
              <p>Orders will appear here when parents purchase items</p>
            </div>
          ) : (
            orders.map(order => (
              <div key={order.id} className="store-order-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
                <div className="order-header">
                  <div>
                    <h4>{order.parentName || order.studentName || 'Unknown'}</h4>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="order-amount">
                    <span className="order-total">{formatCurrency(order.totalAmount)}</span>
                    <span className={`order-status ${order.status?.toLowerCase()}`}>{order.status}</span>
                  </div>
                </div>
                <div className="order-items">
                  {order.items?.map((item, i) => (
                    <div key={i} className="order-item">
                      <span>{item.productName} x{item.quantity}</span>
                      <span>{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="order-meta">
                  <span>Payment: {order.paymentMethod}</span>
                  <span>🏫 {schoolNameFor(order.schoolId) || order.schoolId || 'N/A'}</span>
                  <span style={{ color: '#22c55e', fontSize: '0.85rem' }}>Click for details →</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {view === 'analytics' && analytics && (
        <div className="store-analytics">
          <div className="analytics-cards">
            <div className="analytics-card revenue">
              <span className="analytics-value">{formatCurrency(analytics.totalRevenue)}</span>
              <span className="analytics-label">Total Revenue</span>
            </div>
            <div className="analytics-card">
              <span className="analytics-value">{analytics.totalOrders}</span>
              <span className="analytics-label">Total Orders</span>
            </div>
            <div className="analytics-card">
              <span className="analytics-value">{analytics.completedOrders}</span>
              <span className="analytics-label">Completed</span>
            </div>
            <div className="analytics-card">
              <span className="analytics-value">{analytics.pendingOrders}</span>
              <span className="analytics-label">Pending</span>
            </div>
          </div>

          {Object.keys(analytics.monthlyRevenue).length > 0 && (
            <div className="monthly-chart">
              <h3>Monthly Revenue</h3>
              <div className="chart-bars">
                {Object.entries(analytics.monthlyRevenue).map(([month, revenue]) => {
                  const maxRevenue = Math.max(...Object.values(analytics.monthlyRevenue));
                  const heightPct = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                  return (
                    <div key={month} className="chart-bar-wrapper">
                      <span className="chart-value">{formatCurrency(revenue)}</span>
                      <div className="chart-bar" style={{ height: `${heightPct}%` }}></div>
                      <span className="chart-label">{month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {showAddEdit && (
        <AddEditProductModal
          product={editingProduct}
          onSave={handleSave}
          onClose={() => { setShowAddEdit(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
}


function AddEditProductModal({ product, onSave, onClose }) {
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  const [category, setCategory] = useState(product?.category || 'Uniform');
  const [subcategory, setSubcategory] = useState(product?.subcategory || '');
  const [size, setSize] = useState(product?.size || '');
  const [price, setPrice] = useState(product?.price?.toString() || '');
  const [stockQuantity, setStockQuantity] = useState(product?.stockQuantity?.toString() || '0');
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [imageURL, setImageURL] = useState(product?.imageURL || '');
  const [imagePreview, setImagePreview] = useState(product?.imageURL || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview('');
    setImageURL('');
  };

  const handleSave = async () => {
    if (!name || !price) return;
    setSaving(true);
    let finalImageURL = imageURL;

    if (selectedFile) {
      try {
        setUploading(true);
        const tempId = product?.id || `new_${Date.now()}`;
        finalImageURL = await storeService.uploadProductImage(selectedFile, tempId);
        if (!product?.id) {
          // For new products, we need to save first to get the doc ID,
          // then update with the image URL
          await onSave({
            id: null,
            name,
            description,
            category,
            subcategory: subcategory || null,
            size: size || null,
            price: parseFloat(price),
            stockQuantity: parseInt(stockQuantity) || 0,
            imageURL: finalImageURL,
            isActive,
            createdAt: null,
          });
          setSaving(false);
          setUploading(false);
          return;
        } else {
          // For existing products, update with new image URL
          await onSave({
            id: product.id,
            name,
            description,
            category,
            subcategory: subcategory || null,
            size: size || null,
            price: parseFloat(price),
            stockQuantity: parseInt(stockQuantity) || 0,
            imageURL: finalImageURL,
            isActive,
            createdAt: product.createdAt,
          });
          setSaving(false);
          setUploading(false);
          return;
        }
      } catch (err) {
        setUploadError(err.message || 'Failed to upload image');
        setUploading(false);
        setSaving(false);
        return;
      }
    }

    await onSave({
      id: product?.id || null,
      name,
      description,
      category,
      subcategory: subcategory || null,
      size: size || null,
      price: parseFloat(price),
      stockQuantity: parseInt(stockQuantity) || 0,
      imageURL: finalImageURL || null,
      isActive,
      createdAt: product?.createdAt || null,
    });
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{product ? 'Edit Product' : 'Add Product'}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Photo Picker */}
          <label>Product Photo</label>
          <div className="photo-picker-container">
            {imagePreview ? (
              <div className="photo-preview-wrapper">
                <img src={imagePreview} alt="Product" className="photo-preview" />
                <button type="button" className="remove-photo-btn" onClick={handleRemoveImage}>×</button>
              </div>
            ) : (
              <label className="photo-upload-placeholder">
                <span className="upload-icon">📷</span>
                <span>Click to upload</span>
                <span className="upload-hint">JPG, PNG, GIF, WebP — Max 5MB</span>
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} style={{ display: 'none' }} />
              </label>
            )}
            {imagePreview && !selectedFile && (
              <label className="change-photo-btn">
                <span>Change Photo</span>
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} style={{ display: 'none' }} />
              </label>
            )}
          </div>
          {uploadError && <p className="upload-error">⚠️ {uploadError}</p>}

          <label>Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" />

          <label>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Product description" rows={3} />

          <label>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="Uniform">Uniform</option>
            <option value="Books">Books</option>
            <option value="Crafts">Crafts</option>
            <option value="Accessories">Accessories</option>
            <option value="Stationery">Stationery</option>
            <option value="Other">Other</option>
          </select>

          {category === 'Uniform' && (
            <>
              <label>Type</label>
              <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)}>
                <option value="">None</option>
                {Object.values(UniformSubcategory).map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>

              <label>Size</label>
              <select value={size} onChange={(e) => setSize(e.target.value)}>
                <option value="">None</option>
                {Object.values(ProductSize).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </>
          )}

          <label>Price (EC$)</label>
          <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />

          <label>Stock Quantity</label>
          <input type="number" value={stockQuantity} onChange={(e) => setStockQuantity(e.target.value)} placeholder="0" />

          <label className="checkbox-label">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (visible to parents)
          </label>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave} disabled={saving || uploading || !name || !price}>
            {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderDetailPage({ order, schoolName, onAccept, onReject, onComplete, onCancel, onClose, formatCurrency, formatDate }) {
  const statusColors = {
    'Pending': '#f59e0b',
    'Accepted': '#22c55e',
    'Rejected': '#ef4444',
    'Completed': '#22c55e',
    'Cancelled': '#6b7280',
  };

  return (
    <div className="detail-page">
      <button className="detail-back-btn" onClick={onClose}>◄ Back</button>
      <div className="detail-card" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3>Order Details</h3>
        </div>
        <div className="modal-body">
          {/* Status banner */}
          <div style={{
            background: statusColors[order.status] || '#6b7280',
            color: 'white',
            padding: '10px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: '1.1rem',
          }}>
            {order.status}
          </div>

          {/* Customer info */}
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.9rem' }}>
              <div><strong>School:</strong> {schoolName}</div>
              <div><strong>Parent:</strong> {order.parentName || 'N/A'}</div>
              <div><strong>Student:</strong> {order.studentName || 'N/A'}</div>
              <div><strong>Payment:</strong> {order.paymentMethod || 'N/A'}</div>
              <div><strong>Date:</strong> {formatDate(order.createdAt)}</div>
              {order.parentId && <div><strong>Parent ID:</strong> {order.parentId.substring(0, 12)}...</div>}
              {order.studentId && <div><strong>Student ID:</strong> {order.studentId.substring(0, 12)}...</div>}
            </div>
          </div>

          {/* Items */}
          <h4 style={{ marginBottom: '8px' }}>Items Ordered</h4>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
            {order.items?.map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                borderBottom: i < order.items.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}>
                <div>
                  <div style={{ fontWeight: '500' }}>{item.productName}</div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {formatCurrency(item.price)} each x {item.quantity}
                  </div>
                </div>
                <div style={{ fontWeight: '600' }}>
                  {formatCurrency(item.price * item.quantity)}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#f0fdf4',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>Total</span>
            <span style={{ fontWeight: '700', fontSize: '1.3rem', color: '#22c55e' }}>
              {formatCurrency(order.totalAmount)}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="modal-footer" style={{ flexWrap: 'wrap', gap: '8px' }}>
          <button className="cancel-btn" onClick={onClose}>Close</button>
          {order.status === 'Pending' && (
            <>
              <button
                onClick={onAccept}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#22c55e',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ✓ Accept Order
              </button>
              <button
                onClick={onReject}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#ef4444',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ✕ Reject Order
              </button>
            </>
          )}
          {order.status === 'Accepted' && (
            <>
              <button
                onClick={onComplete}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#22c55e',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ✓ Mark Completed
              </button>
              <button
                onClick={onCancel}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#6b7280',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Cancel Order
              </button>
            </>
          )}
          {(order.status === 'Rejected' || order.status === 'Cancelled') && (
            <span style={{ color: '#6b7280', fontSize: '0.9rem', alignSelf: 'center' }}>
              This order has been {order.status.toLowerCase()}.
            </span>
          )}
          {order.status === 'Completed' && (
            <span style={{ color: '#22c55e', fontSize: '0.9rem', alignSelf: 'center' }}>
              ✓ This order is completed.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default SuperAdminStoreView;
